import { getApiConfigCached } from '../config/api.config';

export const UPBIT_MARKETS_URL =
  'https://api.upbit.com/v1/market/all?is_details=true';
export const BITHUMB_MARKETS_URL =
  'https://api.bithumb.com/v1/market/all?isDetails=true';

export const MARKET_WARNINGS_CACHE_KEY = 'arbitreasure.market-warnings.v1';
export const MARKET_WARNINGS_CACHE_VERSION = 1;
export const MARKET_WARNING_CANONICAL_MAPS_CACHE_KEY =
  'arbitreasure.market-warning-canonical-maps.v1';

const MAX_CACHED_WARNINGS_PER_VENDOR = 1_000;

export type MarketWarningVendor = 'upbit' | 'bithumb';
export type MarketWarningCanonicalMaps = Record<
  MarketWarningVendor,
  Record<string, string>
>;

export interface MarketIdentity {
  exchange: string;
  quote: string;
  base: string;
}

export interface VendorWarningSnapshot {
  warnings: string[];
  updatedAt: number | null;
}

export type MarketWarningsSnapshot = Record<
  MarketWarningVendor,
  VendorWarningSnapshot
>;

interface MarketWarningsCacheV1 {
  version: 1;
  vendors: MarketWarningsSnapshot;
}

export function emptyMarketWarningsSnapshot(): MarketWarningsSnapshot {
  return {
    upbit: { warnings: [], updatedAt: null },
    bithumb: { warnings: [], updatedAt: null },
  };
}

export function emptyMarketWarningCanonicalMaps(): MarketWarningCanonicalMaps {
  return { upbit: {}, bithumb: {} };
}

export function parseMarketWarningCanonicalMaps(
  payload: unknown,
): MarketWarningCanonicalMaps {
  if (!payload || typeof payload !== 'object') {
    throw new Error('Canonical symbol response is not an object');
  }
  const data = (payload as Record<string, unknown>).data;
  if (!data || typeof data !== 'object') {
    throw new Error('Canonical symbol response has no data object');
  }

  const parsed = emptyMarketWarningCanonicalMaps();
  for (const vendor of ['upbit', 'bithumb'] as const) {
    const vendorData = (data as Record<string, unknown>)[vendor];
    if (!vendorData || typeof vendorData !== 'object') continue;
    const mappings = (vendorData as Record<string, unknown>).mappings;
    if (!mappings || typeof mappings !== 'object') continue;
    for (const [canonical, exchangeBase] of Object.entries(mappings)) {
      if (typeof exchangeBase !== 'string') continue;
      const normalizedCanonical = canonical.trim().toUpperCase();
      const normalizedExchangeBase = exchangeBase.trim().toUpperCase();
      if (normalizedCanonical && normalizedExchangeBase) {
        parsed[vendor][normalizedCanonical] = normalizedExchangeBase;
      }
    }
  }
  return parsed;
}

export function resolveMarketWarningBase(
  vendor: MarketWarningVendor,
  canonicalBase: string,
  canonicalMaps: MarketWarningCanonicalMaps,
): string {
  const normalized = canonicalBase.trim().toUpperCase();
  return canonicalMaps[vendor][normalized] ?? normalized;
}

export async function fetchMarketWarningCanonicalMaps(
  signal?: AbortSignal,
  apiBaseUrl = getApiConfigCached().apiUrl,
): Promise<MarketWarningCanonicalMaps> {
  const url = `${apiBaseUrl.replace(/\/$/, '')}/resolve/canonicals`;
  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error(`Canonical symbol request failed (${response.status})`);
  }
  return parseMarketWarningCanonicalMaps(await response.json());
}

export function normalizeWarningVendor(
  exchange: string | null | undefined,
): MarketWarningVendor | null {
  if (!exchange) return null;
  const normalized = exchange.trim().toLowerCase().replace(/[._-]/g, '');
  if (normalized === 'upbit') return 'upbit';
  if (normalized === 'bithumb') return 'bithumb';
  return null;
}

export function marketWarningKey(
  exchange: string,
  quote: string,
  base: string,
): string | null {
  const vendor = normalizeWarningVendor(exchange);
  const normalizedQuote = quote.trim().toUpperCase();
  const normalizedBase = base.trim().toUpperCase();
  if (!vendor || !normalizedQuote || !normalizedBase) return null;
  return `${vendor}:${normalizedQuote}:${normalizedBase}`;
}

function parseMarket(market: unknown): { quote: string; base: string } | null {
  if (typeof market !== 'string') return null;
  const parts = market.split('-');
  if (parts.length !== 2) return null;
  const quote = parts[0].trim().toUpperCase();
  const base = parts[1].trim().toUpperCase();
  if (!quote || !base) return null;
  return { quote, base };
}

export function parseUpbitMarketWarnings(payload: unknown): string[] {
  if (!Array.isArray(payload)) {
    throw new Error('Upbit market list response is not an array');
  }

  const warnings = new Set<string>();
  let recognizedMarkets = 0;
  for (const entry of payload) {
    if (!entry || typeof entry !== 'object') continue;
    const record = entry as Record<string, unknown>;
    const event = record.market_event;
    if (!event || typeof event !== 'object') continue;
    const warning = (event as Record<string, unknown>).warning;
    if (typeof warning !== 'boolean') continue;
    const pair = parseMarket(record.market);
    if (!pair) continue;
    recognizedMarkets += 1;
    if (!warning) continue;
    const key = marketWarningKey('upbit', pair.quote, pair.base);
    if (key) warnings.add(key);
  }
  if (payload.length > 0 && recognizedMarkets === 0) {
    throw new Error('Upbit market list response has no recognized markets');
  }
  return [...warnings].sort();
}

export function parseBithumbMarketWarnings(payload: unknown): string[] {
  if (!Array.isArray(payload)) {
    throw new Error('Bithumb market list response is not an array');
  }

  const warnings = new Set<string>();
  let recognizedMarkets = 0;
  for (const entry of payload) {
    if (!entry || typeof entry !== 'object') continue;
    const record = entry as Record<string, unknown>;
    if (typeof record.market_warning !== 'string') continue;
    const pair = parseMarket(record.market);
    if (!pair) continue;
    recognizedMarkets += 1;
    if (record.market_warning !== 'CAUTION') continue;
    const key = marketWarningKey('bithumb', pair.quote, pair.base);
    if (key) warnings.add(key);
  }
  if (payload.length > 0 && recognizedMarkets === 0) {
    throw new Error('Bithumb market list response has no recognized markets');
  }
  return [...warnings].sort();
}

export async function fetchVendorMarketWarnings(
  vendor: MarketWarningVendor,
  signal?: AbortSignal,
): Promise<string[]> {
  const url = vendor === 'upbit' ? UPBIT_MARKETS_URL : BITHUMB_MARKETS_URL;
  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error(`${vendor} market list request failed (${response.status})`);
  }
  const payload: unknown = await response.json();
  return vendor === 'upbit'
    ? parseUpbitMarketWarnings(payload)
    : parseBithumbMarketWarnings(payload);
}

function validVendorSnapshot(
  value: unknown,
  vendor: MarketWarningVendor,
): VendorWarningSnapshot | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  if (!Array.isArray(record.warnings)) return null;
  if (
    record.updatedAt !== null &&
    (typeof record.updatedAt !== 'number' || !Number.isFinite(record.updatedAt))
  ) {
    return null;
  }

  const warnings = new Set<string>();
  for (const warning of record.warnings) {
    if (typeof warning !== 'string') continue;
    const [keyVendor, quote, base, ...extra] = warning.split(':');
    if (keyVendor !== vendor || extra.length !== 0) continue;
    const normalized = marketWarningKey(vendor, quote ?? '', base ?? '');
    if (normalized) warnings.add(normalized);
    if (warnings.size >= MAX_CACHED_WARNINGS_PER_VENDOR) break;
  }

  return {
    warnings: [...warnings].sort(),
    updatedAt: record.updatedAt as number | null,
  };
}

export function loadMarketWarningsCache(
  storage?: Pick<Storage, 'getItem'> | null,
): MarketWarningsSnapshot {
  const empty = emptyMarketWarningsSnapshot();
  if (storage === undefined) {
    try {
      storage = typeof window === 'undefined' ? null : window.localStorage;
    } catch {
      return empty;
    }
  }
  if (!storage) return empty;
  try {
    const raw = storage.getItem(MARKET_WARNINGS_CACHE_KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as Partial<MarketWarningsCacheV1>;
    if (parsed.version !== MARKET_WARNINGS_CACHE_VERSION || !parsed.vendors) {
      return empty;
    }
    return {
      upbit: validVendorSnapshot(parsed.vendors.upbit, 'upbit') ?? empty.upbit,
      bithumb:
        validVendorSnapshot(parsed.vendors.bithumb, 'bithumb') ?? empty.bithumb,
    };
  } catch {
    return empty;
  }
}

export function saveMarketWarningsCache(
  snapshot: MarketWarningsSnapshot,
  storage?: Pick<Storage, 'setItem'> | null,
): void {
  if (storage === undefined) {
    try {
      storage = typeof window === 'undefined' ? null : window.localStorage;
    } catch {
      return;
    }
  }
  if (!storage) return;
  const cache: MarketWarningsCacheV1 = {
    version: MARKET_WARNINGS_CACHE_VERSION,
    vendors: snapshot,
  };
  try {
    storage.setItem(MARKET_WARNINGS_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // localStorage is a best-effort last-known-good cache.
  }
}

export function loadMarketWarningCanonicalMapsCache(
  storage?: Pick<Storage, 'getItem'> | null,
): MarketWarningCanonicalMaps {
  const empty = emptyMarketWarningCanonicalMaps();
  if (storage === undefined) {
    try {
      storage = typeof window === 'undefined' ? null : window.localStorage;
    } catch {
      return empty;
    }
  }
  if (!storage) return empty;
  try {
    const raw = storage.getItem(MARKET_WARNING_CANONICAL_MAPS_CACHE_KEY);
    return raw ? parseMarketWarningCanonicalMaps(JSON.parse(raw)) : empty;
  } catch {
    return empty;
  }
}

export function saveMarketWarningCanonicalMapsCache(
  maps: MarketWarningCanonicalMaps,
  storage?: Pick<Storage, 'setItem'> | null,
): void {
  if (storage === undefined) {
    try {
      storage = typeof window === 'undefined' ? null : window.localStorage;
    } catch {
      return;
    }
  }
  if (!storage) return;
  try {
    storage.setItem(
      MARKET_WARNING_CANONICAL_MAPS_CACHE_KEY,
      JSON.stringify({
        data: {
          upbit: { mappings: maps.upbit },
          bithumb: { mappings: maps.bithumb },
        },
      }),
    );
  } catch {
    // localStorage is a best-effort last-known-good cache.
  }
}
